import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BookOpenIcon, BriefcaseIcon, ScaleIcon } from '@heroicons/react/24/outline';
import { useAppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { EinstLink } from './einstellungen/EinstLink';
import { ORGANISATION_PFAD } from './einstellungen/seiten';
import { alterChatModus, enterSendet, mitEnterSenden } from './einstellungen/praeferenzen';
import { GUTHABEN_TEXTE } from '../administration/billing/guthabenTexte';
import { VorlagenKnopf } from './vorlagen/VorlagenKnopf';
import { vorlagenApi } from './vorlagen/vorlagenApi';
import { ersterPlatzhalter, startBeispiele, type Beispielkarte } from './vorlagen/vorlagenTexte';
import { GUTE_FRAGE_FENSTER } from './rechercheTipps';
import LegalLinks from '../components/LegalLinks';
import { ErklaerFenster, InfoTipp } from '../components/Erklaerung';
import { useWebsucheAn, WebsucheHinweis } from './websucheHinweis';
import './Dashboard.css';

// Profil-Nachbau F-20.3 (BAU-VORLAGEN.md 5.1, V-7): „Mit einem Beispiel beginnen“ zeigt die veröffentlichten Beispiele mit
// „Auf der Startseite“ (höchstens drei). Bis zur Antwort, bei Fehler, ohne Beispiel und in einer Sicht bleiben die drei
// festen Beispiele (startBeispiele in pages/vorlagen/vorlagenTexte.ts); das Symbol nennt die Karte selbst.
const SYMBOLE = { BookOpenIcon, ScaleIcon, BriefcaseIcon } as const;
const vorlageHinweis = (titel: string) => `Vorlage „${titel}“ übernommen. Sie können die Frage jetzt anpassen.`;
// Die Auswahl „Vorlage einfügen“ liegt im Formular der Startseite: Enter in ihrem Suchfeld schickt nie die Frage ab.
const keinAbsenden = (event: KeyboardEvent) => { if (event.key === 'Enter' && event.target instanceof HTMLInputElement) event.preventDefault(); };

// F-24 (PF-TX-rahmen-3): die Tipps stehen im Fenster GUTE_FRAGE_FENSTER (rechercheTipps.ts) rechts in der oberen Zeile; Spalte und
// Dialog „Recherchetipps“ sind entfallen.

export default function Dashboard() {
  const { onNewChat } = useAppLayout();
  // Plan 1a Welle 2, Ä-30 (SPEC-USER.md 5.6): Punkt 1 – woher die Antworten kommen und wer dahintersteht, mit dem
  // Produktnamen aus der Marke (MK-1). Punkt 3 – wer die Admin-Einstellungen einer Firma hat (Ebenenobjekt des Servers, nie
  // ein Rollenname), erreicht sie von hier mit einem ruhigen Link; er öffnet sie im Pop-up wie der Eintrag im Kontomenü.
  const { user } = useAuth();
  const firmaZumVerwalten = user?.ebenen?.adminEinstellungen?.firmaName;
  // Profil-Nachbau F-09 (BAU-PRAEFERENZEN.md 1.4, F-P4): die Startseite folgt derselben Tastenregel wie das Eingabefeld der
  // Recherche („Mit Enter senden“ am Konto, sonst die frühere Wahl im Browser, sonst aus).
  const sendetMitEnter = mitEnterSenden(user?.account?.sendWithEnter, alterChatModus());
  const [question, setQuestion] = useState('');
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState('');
  const [exampleNotice, setExampleNotice] = useState('');
  const questionRef = useRef<HTMLTextAreaElement>(null);
  const openingRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const sicht = !!user?.sicht?.aktiv;
  const websucheAn = useWebsucheAn();
  const [beispiele, setBeispiele] = useState<Beispielkarte[]>(() => startBeispiele());
  // V-6: nach dem nächsten Rendern markiert das Feld den ersten Platzhalter, ohne Platzhalter steht die Marke am Textende.
  const [markierung, setMarkierung] = useState<{ start: number; ende: number; nr: number } | null>(null);

  // Die Startseite wartet nie auf die Beispiele des Betreibers: zuerst die festen, nach der Antwort die vom Server.
  useEffect(() => {
    if (sicht) return;
    const abbruch = new AbortController();
    vorlagenApi.startseite(abbruch.signal).then(antwort => { if (!abbruch.signal.aborted) setBeispiele(startBeispiele(antwort)); }, () => undefined);
    return () => abbruch.abort();
  }, [sicht]);

  useEffect(() => {
    if (!markierung) return;
    const feld = questionRef.current;
    feld?.focus({ preventScroll: true });
    feld?.setSelectionRange(markierung.start, markierung.ende);
  }, [markierung]);

  const uebernehmen = (text: string, hinweis: string) => {
    setQuestion(text);
    setError('');
    setExampleNotice(hinweis);
    const platzhalter = ersterPlatzhalter(text) ?? { start: text.length, ende: text.length };
    setMarkierung(alt => ({ ...platzhalter, nr: (alt?.nr ?? 0) + 1 }));
  };

  // 5.1: „Verwenden“ auf der Seite „Vorlagen“ kommt mit state.vorlage. Der Text wird einmal übernommen, danach ersetzt die Seite
  // den Verlaufseintrag ohne Zustand, damit ein Neuladen ihn nicht noch einmal einsetzt.
  const vorlage = (location.state as { vorlage?: { titel?: unknown; text?: unknown } } | null)?.vorlage;
  useEffect(() => {
    if (!vorlage || typeof vorlage.titel !== 'string' || typeof vorlage.text !== 'string') return;
    uebernehmen(vorlage.text, vorlageHinweis(vorlage.titel));
    navigate('.', { replace: true, state: null });
  }, [vorlage]);

  const openResearch = async () => {
    if (openingRef.current) return;
    const query = question.trim();
    if (query.length < 10) {
      setError('Bitte geben Sie eine aussagekräftige Frage mit mindestens 10 Zeichen ein.');
      questionRef.current?.focus({ preventScroll: true });
      return;
    }
    openingRef.current = true;
    setIsOpening(true);
    setError('');
    try {
      await onNewChat(query);
    } catch {
      setError('Die Recherche konnte nicht gestartet werden. Bitte versuchen Sie es erneut.');
    } finally {
      openingRef.current = false;
      setIsOpening(false);
    }
  };

  const chooseExample = (example: Beispielkarte) => {
    uebernehmen(example.question, `Beispielfrage zu ${example.title} übernommen. Sie können die Frage jetzt anpassen.`);
    if (example.id) void vorlagenApi.beispielVerwendet(example.id).catch(() => undefined);
  };

  return (
    <div className="research-home">
      <header className="home-heading">
        <div className="home-heading-topline">
          <p className="home-eyebrow">Ihr Rechercheplatz</p>
          <div className="home-heading-wege">
            {firmaZumVerwalten && <EinstLink to={ORGANISATION_PFAD} className="home-verwalten">{firmaZumVerwalten} verwalten</EinstLink>}
            <ErklaerFenster {...GUTE_FRAGE_FENSTER} />
          </div>
        </div>
        <h1>Was möchten Sie <em>klären?</em></h1>
        {/* Spur guthaben, GU-29 (SPEC-KONTINGENT.md 14.1 Punkt 5): nach dem Kontingent bezahlt das zusätzliche Guthaben der Firma
            die Recherchen; der Satz steht ohne Betrag auf der Startseite. */}
        {user?.access?.ueberGuthaben === true && <p className="home-herkunft" role="status" data-guthaben>{GUTHABEN_TEXTE.mitglieder}</p>}
      </header>

      <div className="home-workspace">
        <div className="home-research">
          <form className="home-question-form" onSubmit={event => { event.preventDefault(); void openResearch(); }} aria-busy={isOpening}>
            <label htmlFor="home-question">Ihre Rechtsfrage</label>
            <textarea
              id="home-question"
              name="researchQuestion"
              ref={questionRef}
              value={question}
              onChange={event => { setQuestion(event.target.value); setError(''); }}
              onKeyDown={event => { if (enterSendet({ key: event.key, shiftKey: event.shiftKey, isComposing: event.nativeEvent.isComposing }, sendetMitEnter)) { event.preventDefault(); void openResearch(); } }}
              placeholder="Beschreiben Sie Ihren Sachverhalt oder stellen Sie eine konkrete Frage …"
              autoComplete="off"
              rows={3}
              aria-describedby={[ 'home-question-hint', websucheAn ? 'home-websuche' : '', error ? 'home-question-error' : '' ].filter(Boolean).join(' ')}
              aria-invalid={error ? true : undefined}
              disabled={isOpening}
            />
            <div className="home-question-footer">
              {/* F-24 (PF-TX-rahmen-3, Ä-30 Punkt 1): der KI-Hinweis wie bei meinGPT direkt unter dem Feld; Herkunft und Anbieter im
                  Tooltip daneben (vorher Absatz unter der Überschrift). */}
              <div className="home-ki-zeile">
                <p id="home-question-hint">KI kann Fehler machen. Prüfen Sie die angeführten Quellen.</p>
                <InfoTipp wort="Quellen" text="Die KI durchsucht das Rechtsinformationssystem des Bundes (RIS) und weitere Quellen." />
              </div>
              <div className="home-question-aktionen" onKeyDown={keinAbsenden}>
                <VorlagenKnopf mitText feldHatText={question.trim().length > 0} disabled={isOpening}
                  onWahl={wahl => uebernehmen(wahl.text, vorlageHinweis(wahl.titel))} />
                <button type="submit" className="home-open-chat" disabled={isOpening}>
                  <span>{isOpening ? 'Recherche startet …' : 'Recherche starten'}</span>
                  <ArrowRightIcon aria-hidden="true" />
                </button>
              </div>
            </div>
            {websucheAn && <WebsucheHinweis id="home-websuche" className="home-websuche" />}
            {error && <p className="home-error" id="home-question-error" role="alert">{error}</p>}
          </form>

          <section className="home-examples" aria-labelledby="home-examples-title">
            <div className="home-section-label">
              <h2 id="home-examples-title">Mit einem Beispiel beginnen</h2>
            </div>
            <div className="home-example-grid">
              {beispiele.map(example => {
                const Bild = SYMBOLE[example.symbol];
                return (
                  <button
                    type="button"
                    key={example.id ?? example.title}
                    className="home-example"
                    onClick={() => chooseExample(example)}
                    disabled={isOpening}
                    aria-label={`${example.title}: Beispielfrage übernehmen – ${example.question}`}
                  >
                    <span className="home-example-title"><Bild aria-hidden="true" /><span>{example.title}</span><ArrowRightIcon className="home-example-arrow" aria-hidden="true" /></span>
                    <span className="home-example-scope">{example.scope}</span>
                    <span className="home-example-question">{example.question}</span>
                  </button>
                );
              })}
            </div>
            <Link to="/vorlagen" className="home-alle-vorlagen">Alle Vorlagen und Beispiele</Link>
            <p className="sr-only" aria-live="polite">{exampleNotice}</p>
          </section>
        </div>
      </div>

      <footer className="home-footer">
        {/* F-23 (Nutzerblick Z-05, H-04, H-20; erster Eindruck): Datenschutz, Impressum und AGB, nur mit Adresse (config/legal.ts).
            F-19 (PF-SL3-rahmen-1): jeder Link im neuen Fenster, damit eine angefangene Frage bleibt. */}
        <LegalLinks className="home-legal" neuesFenster />
      </footer>
    </div>
  );
}
